import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { toTitleCase } from 'codelyzer/util/utils';
import * as mapbox from 'mapbox-gl';
import { filter, take } from 'rxjs/operators';
import { TodayDemand } from 'src/app/types';
import { RequestDriverTransferComponent } from '../request-driver-transfer/request-driver-transfer.component';

@Component({
  selector: 'lohi-driver-demand-map',
  templateUrl: './driver-demand-map.component.html',
  styleUrls: ['./driver-demand-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverDemandMapComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChildren('map') private mapElements: QueryList<ElementRef<HTMLDivElement>>;
  private map: mapbox.Map;
  @Input() public regionsWithDemand: TodayDemand[];

  constructor(private dialog: MatDialog) {
    (mapbox as any).accessToken =
      'pk.eyJ1Ijoic2F0ZXN0ZGV2IiwiYSI6ImNrYmVraDllZDBtcXQycHFlbzVqYXk0cnMifQ.HzVySDvy0mLDW03ykD2HNw';
  }

  public ngOnInit(): void {}

  public ngAfterViewInit(): void {
    this.initMap();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.recalculateMap();
  }

  private async initMap() {
    // Make sure we have a map element
    if (this.mapElements.length === 0) {
      await this.mapElements.changes
        .pipe(
          filter((changes: QueryList<any>) => {
            return changes.length > 0;
          }),
          take(1),
        )
        .toPromise();
    }

    const options: mapbox.MapboxOptions = {
      container: this.mapElements.first.nativeElement,
      style: 'mapbox://styles/mapbox/streets-v11',
    };

    await new Promise((resolve) => setTimeout(resolve, 100));

    this.map = new mapbox.Map(options);
    this.map.setZoom(4);
    this.map.setCenter({ lat: 39.8283, lng: -98.5795 });
    this.map.addControl(new mapbox.NavigationControl());
    this.map.on('click', 'regions', (e) => {
      if (e.features[0]?.properties?.id) {
        // this.regionSelected.next(e.features[0]?.properties?.id);
      }
    });
    this.map.on('load', () => {
      this.recalculateMap();
    });
  }

  public openRequestTransferModal(regionId: number, regionName: string) {
    this.dialog.open(RequestDriverTransferComponent, {
      data: { regionId, regionName },
    });
  }

  private recalculateMap() {
    if (this.regionsWithDemand && this.map) {
      const regions = this.regionsWithDemand.filter((region) => {
        // if (this.selectedRegionId) {
        //   return region.recommendedShiftId === this.selectedRegionId;
        // }
        return true;
      });
      const bounds = new mapbox.LngLatBounds();
      (regions || []).forEach((region) => {
        bounds.extend([region.regionLocation.longitude, region.regionLocation.latitude]);
      });
      if (!bounds.isEmpty()) {
        this.map.setMaxZoom(8);
        this.map.fitBounds(bounds, { padding: 75 });
        this.map.setMaxZoom(15);
        if (this.map.getLayer('regions')) {
          // just a repaint
          (this.map.getSource('regions') as mapbox.GeoJSONSource).setData(getRegionsSource(regions).data);
        } else {
          this.map.addSource('regions', getRegionsSource(regions));
          this.map.addLayer({
            id: `regions`,
            type: 'circle',
            source: `regions`,
            paint: {
              'circle-radius': ['interpolate', ['exponential', 2], ['zoom'], 0, 0, 20, ['*', ['get', 'radius'], 1]],
              'circle-color': ['get', 'circleColor'],
            },
          });
          this.map.addLayer({
            id: 'regions-labels',
            type: 'symbol',
            source: 'regions',
            layout: {
              'text-field': ['get', 'demand'],
              'text-justify': 'center',
              'text-size': 10,
            },
            paint: {
              'text-color': '#ffffff',
            },
          });

          const popUp = new mapbox.Popup({
            closeButton: false,
            closeOnClick: true,
            maxWidth: '300px',
          });

          this.map.on('mouseenter', 'regions', (e) => {
            const coordinates = (e.features[0].geometry as any).coordinates.slice();
            const description = e.features[0].properties.description;
            const regionId = e.features[0].properties.id;
            const regionName = e.features[0].properties.name;

            // Ensure that if the map is zoomed out such that multiple
            // copies of the feature are visible, the popup appears
            // over the copy being pointed to.
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
              coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }

            popUp.setLngLat(coordinates).setHTML(description).addTo(this.map);
            popUp
              .getElement()
              .getElementsByClassName('request-do-not-use-it')[0]
              .addEventListener('click', () => {
                this.openRequestTransferModal(regionId, regionName);
              });
          });

          // this.map.on('mouseleave', 'regions', () => {
          //   popUp.remove();
          // });
        }
      }
    }
  }
}

function getRegionsSource(regions: TodayDemand[]): mapboxgl.GeoJSONSourceRaw {
  const regionData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    type: 'FeatureCollection',
    features: regions.map((region) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [region.regionLocation.longitude, region.regionLocation.latitude],
      },
      properties: {
        id: region.regionId,
        name: region.regionName,
        count: region.driversNeededToday,
        demand: getCircleText(region.demandLevel),
        circleColor: getDemandLevelColor(region.demandLevel),
        radius: getRadiusInMetersBasedOnDemand(region.demandLevel, region.regionLocation.latitude),
        description: popupHtml(region),
      },
    })),
  };
  return {
    type: 'geojson',
    data: regionData,
  };
}

// From https://stackoverflow.com/questions/37599561/drawing-a-circle-with-the-radius-in-miles-meters-with-mapbox-gl-js
function getRadiusInMetersBasedOnDemand(demandLevel: string, latitude: number) {
  let meters = 100000;
  if (demandLevel === 'high') {
    meters = 300000;
  } else if (demandLevel === 'medium') {
    meters = 200000;
  }
  return meters / (78271.484 / 2 ** 20) / Math.cos((latitude * Math.PI) / 180);
}

// function getCircleColor(driversNeededToday: number): string {
//   if (driversNeededToday > 0) {
//     return 'rgba(68,138,255, 1)';
//   } else {
//     return 'rgba(180,193,204, 1)';
//   }
// }

function getDemandLevelColor(demandLevel: string): string {
  if (demandLevel === 'high') {
    return 'rgba(240,79,15, 1)';
  } else if (demandLevel === 'medium') {
    return 'rgba(68,138,255, 1)';
  } else {
    return 'rgba(180,193,204, 1)';
  }
}

function currencyInDollar(pennies: number): string {
  const dollars = Math.round(pennies / 100);
  return '$' + dollars;
}

function viewVisibility(show: boolean): string {
  if (!show) {
    return 'none';
  }
}

function getCircleText(demandLevel: string): string {
  let circleText = demandLevel;
  if (demandLevel === 'medium') {
    circleText = 'med';
  }
  return circleText.toUpperCase();
}

function popupHtml(region: TodayDemand): string {
  return `<div class="flex flex-col"><span style="background: ${getDemandLevelColor(
    region.demandLevel,
  )}" class="text-base py-1 px-2 text-white font-bold rounded-t">${region.regionName} - ${toTitleCase(
    region.demandLevel,
  )} Demand</span></div><div class="flex flex-col p-2"><div class="flex flex-row mb-2 items-center" style="display: ${viewVisibility(
    region.deadheadPaid || region.driversNeededToday > 0,
  )}"><div class="h-4 w-4 bg-gray-800 rounded-full flex items-center justify-center mr-1"><span class="material-icons text-white self-center text-lg">local_shipping</span></div><div class="flex flex-col text-sm font-bold text-gray-800"><span style="display: ${viewVisibility(
    region.driversNeededToday > 0,
  )}">${region.driversNeededToday} Drivers Needed</span><span style="display: ${viewVisibility(
    region.deadheadPaid,
  )}">Deadhead Paid!</span></div></div><div class="flex flex-row mb-2 justify-between"><div class="flex flex-col text-gray-600 text-sm mr-2"><span>Your Drivers:</span><span>Avg. Earnings/Shift:</span><span>Next Week Demand:</span></div><div class="flex flex-col text-sm"><span class="text-gray-800">${
    region.yourDrivers
  }</span><span class="text-gray-800">${currencyInDollar(
    region.pennyPerShift,
  )}</span><span class="font-bold" style="color: ${getDemandLevelColor(region.demandLevelNextWeek)}">${toTitleCase(
    region.demandLevelNextWeek,
  )}</span></div></div><div style="display: ${viewVisibility(
    region.driversNeededToday > 0 || region.driversNeededNextWeek > 0,
  )}" class="request-do-not-use-it text-blue-600 text-xs py-1 border border-gray-400 rounded w-full text-center cursor-pointer">Request Transfer</div></div>`;
}
